import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { peopleApi, type RoleExplanationResponse } from '@/api/peopleWrapper';
import { getRoleDisplayName } from '@/utils/rolePermissions';
import type { RoleExplanationResponseSourcesInnerTypeEnum } from 'we-tree-api-client/v2';
import './role-explanation-modal.css';

/** Deep link to the Roles and Permissions section in User Documentation */
const ROLES_DOCS_LINK = { pathname: '/help', search: '?tab=docs', hash: '#roles-permissions' };

interface RoleExplanationModalProps {
  familyMemberId: string;
  isOpen: boolean;
  onClose: () => void;
}

const RoleExplanationModal: React.FC<RoleExplanationModalProps> = ({ familyMemberId, isOpen, onClose }) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const [explanation, setExplanation] = useState<RoleExplanationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && familyMemberId) {
      setIsLoading(true);
      setError(null);
      setExplanation(null);

      peopleApi
        .getRoleExplanation(familyMemberId)
        .then((result) => {
          if (result.success) {
            setExplanation(result.data);
          } else {
            setError(result.error?.getUserMessage() || t('roleExplanation.loadFailed'));
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Error loading role explanation:', err);
          setError(t('roleExplanation.unexpectedError'));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, familyMemberId, t]);

  if (!isOpen) return null;

  const formatRelationshipGrouping = (grouping?: string): string => {
    if (!grouping) return '';
    return t(`roleExplanation.grouping.${grouping}`, { defaultValue: grouping });
  };

  const formatSourceType = (type: RoleExplanationResponseSourcesInnerTypeEnum): string => {
    return t(`roleExplanation.sourceType.${type}`, { defaultValue: type });
  };

  return (
    <div className="v01-modal-overlay v01-modal-overlay--centered" onClick={onClose}>
      <div className="v01-modal role-explanation-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="role-explanation-modal-header">
          <h2 className="v01-modal__title">{t('roleExplanation.title')}</h2>
          <button type="button" onClick={onClose} className="v01-modal__close" aria-label={tCommon('close')}>
            ×
          </button>
        </div>

        <div className="role-explanation-modal-body">
          {isLoading && (
            <div className="role-explanation-modal-loading">
              <div className="v01-modal__spinner" />
              <p>{t('roleExplanation.loading')}</p>
            </div>
          )}

          {error && (
            <div className="v01-modal__error">
              <p>{error}</p>
            </div>
          )}

          {explanation && !isLoading && (
            <>
              {/* Effective Role Badge */}
              <div className="role-explanation-modal-effective-role">
                <div className="role-explanation-modal-effective-role-label">{t('roleExplanation.yourRole')}</div>
                <div
                  className={`role-explanation-modal-role-badge role-explanation-modal-role-badge--${explanation.effectiveRole}`}
                >
                  {getRoleDisplayName(explanation.effectiveRole)}
                </div>
              </div>

              {/* Summary */}
              <div className="role-explanation-modal-summary">
                {explanation.effectiveRole === 'denied' ? (
                  <p>{t('roleExplanation.accessDenied')}</p>
                ) : explanation.effectiveRole === 'viewer' ? (
                  <>
                    <p>{t('roleExplanation.accessViewer')}</p>
                    {explanation.sources.some(
                      (s) =>
                        (s.details as { relationshipGrouping?: string } | undefined)?.relationshipGrouping ===
                        'all_users'
                    ) && (
                      <p className="role-explanation-modal-summary-hint">
                        {t('roleExplanation.viewerNoRelationshipHint')}
                      </p>
                    )}
                  </>
                ) : explanation.effectiveRole === 'contributor' ? (
                  <p>
                    {t('roleExplanation.accessContributorBefore')} <strong>{t('person.suggestChanges')}</strong>
                    {t('roleExplanation.accessContributorAfter')}
                  </p>
                ) : (
                  <p>{t('roleExplanation.accessEditor')}</p>
                )}
              </div>

              {/* Link to full explanation in User Documentation */}
              <div className="role-explanation-modal-docs-link">
                <Link to={ROLES_DOCS_LINK} className="role-explanation-modal-docs-link-a" onClick={onClose}>
                  {t('roleExplanation.learnMore')}
                </Link>
              </div>

              {/* Blocks */}
              {explanation.blocks.length > 0 && (
                <div className="role-explanation-modal-section">
                  <h3 className="role-explanation-modal-section-title">{t('roleExplanation.accessRestrictions')}</h3>
                  <div className="role-explanation-modal-blocks">
                    {explanation.blocks.map((block, index) => (
                      <div key={index} className="role-explanation-modal-block-item">
                        <div className="role-explanation-modal-block-icon">🚫</div>
                        <div className="role-explanation-modal-block-content">
                          <div className="role-explanation-modal-block-source">{block.source}</div>
                          {block.reason && <div className="role-explanation-modal-block-reason">{block.reason}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sources */}
              {explanation.sources.length > 0 && (
                <div className="role-explanation-modal-section">
                  <h3 className="role-explanation-modal-section-title">{t('roleExplanation.howDetermined')}</h3>
                  <div className="role-explanation-modal-sources">
                    {explanation.sources.map((source, index) => (
                      <div key={index} className="role-explanation-modal-source-item">
                        <div className="role-explanation-modal-source-header">
                          <div className="role-explanation-modal-source-type">{formatSourceType(source.type)}</div>
                          <div
                            className={`role-explanation-modal-role-badge role-explanation-modal-role-badge--${source.role}`}
                          >
                            {getRoleDisplayName(source.role)}
                          </div>
                        </div>
                        <div className="role-explanation-modal-source-description">{source.description}</div>
                        {source.details && (
                          <div className="role-explanation-modal-source-details">
                            {source.details.relationshipGrouping && (
                              <div className="role-explanation-modal-detail">
                                <span className="role-explanation-modal-detail-label">
                                  {t('roleExplanation.relationship')}
                                </span>
                                <span className="role-explanation-modal-detail-value">
                                  {formatRelationshipGrouping(source.details.relationshipGrouping)}
                                </span>
                              </div>
                            )}
                            {source.details.livingStatus && (
                              <div className="role-explanation-modal-detail">
                                <span className="role-explanation-modal-detail-label">
                                  {t('roleExplanation.livingStatus')}
                                </span>
                                <span className="role-explanation-modal-detail-value">
                                  {source.details.livingStatus}
                                </span>
                              </div>
                            )}
                            {source.details.assignedBy && (
                              <div className="role-explanation-modal-detail">
                                <span className="role-explanation-modal-detail-label">
                                  {t('roleExplanation.assignedBy')}
                                </span>
                                <span className="role-explanation-modal-detail-value">{source.details.assignedBy}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Grants */}
              {explanation.grants.length > 0 && (
                <div className="role-explanation-modal-section">
                  <h3 className="role-explanation-modal-section-title">{t('roleExplanation.permissionGrants')}</h3>
                  <div className="role-explanation-modal-grants">
                    {explanation.grants.map((grant, index) => (
                      <div key={index} className="role-explanation-modal-grant-item">
                        <div className="role-explanation-modal-grant-type">{grant.type}</div>
                        <div className="role-explanation-modal-grant-source">{grant.source}</div>
                        <div
                          className={`role-explanation-modal-role-badge role-explanation-modal-role-badge--${grant.role}`}
                        >
                          {getRoleDisplayName(grant.role)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="v01-modal__footer">
          <button type="button" onClick={onClose} className="btn btn--ghost">
            {tCommon('close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleExplanationModal;
